import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Ban } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatCurrency, formatDate } from "@/utils/format";
import {
  patchInadimplenciaCliente,
  type InadimplenciaClienteRow,
  type InadimplenciaParcela,
} from "@/services/inadimplencia";
import {
  REGUA_LABEL,
  SPC_LABEL,
  bloqueiaNovoCredito,
  type SpcStatus,
} from "../inadimplenciaUtils";

const SPC_OPTIONS: SpcStatus[] = [
  "nao_consultado",
  "sem_restricao",
  "negativado_por_nos",
  "negativado_por_outros",
];

interface Props {
  open: boolean;
  onClose: () => void;
  cliente: InadimplenciaClienteRow | null;
  boletos: InadimplenciaParcela[];
  onUpdated: () => void;
}

export function ClienteDrawer({ open, onClose, cliente, boletos, onUpdated }: Props) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [spcStatus, setSpcStatus] = useState<SpcStatus>(cliente?.spcStatus ?? "nao_consultado");
  const [observacao, setObservacao] = useState(cliente?.acordoObservacao ?? "");
  const [acordoAtivo, setAcordoAtivo] = useState(Boolean(cliente?.emAcordo));

  useEffect(() => {
    setSpcStatus(cliente?.spcStatus ?? "nao_consultado");
    setObservacao(cliente?.acordoObservacao ?? "");
    setAcordoAtivo(Boolean(cliente?.emAcordo));
  }, [cliente?.clientKey, cliente?.spcStatus, cliente?.acordoObservacao, cliente?.emAcordo]);

  const titulos = useMemo(() => {
    if (!cliente) return [];
    return boletos
      .filter((b) => b.clientKey === cliente.clientKey)
      .sort((a, b) => a.dueDate.localeCompare(b.dueDate));
  }, [boletos, cliente]);

  if (!cliente) return null;

  const save = async (patch: Parameters<typeof patchInadimplenciaCliente>[1]) => {
    setSaving(true);
    setError(null);
    try {
      const saved = await patchInadimplenciaCliente(cliente.clientKey, {
        clientId: cliente.clientId,
        clientName: cliente.clientName,
        ...patch,
      });
      if (saved.acordoAtivo != null) setAcordoAtivo(saved.acordoAtivo);
      if (saved.spcStatus) setSpcStatus(saved.spcStatus);
      onUpdated();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={cliente.clientName}
      size="xl"
      closeOnEscape
      closeOnBackdropClick
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
            {REGUA_LABEL[cliente.regua]}
          </span>
          {bloqueiaNovoCredito(cliente.regua) && !acordoAtivo && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2.5 py-1 font-medium text-amber-800">
              <Ban className="h-3 w-3" />
              Bloqueia novo crédito
            </span>
          )}
          {acordoAtivo && (
            <span className="rounded-full bg-sky-50 px-2.5 py-1 font-medium text-sky-800">
              Em acordo
            </span>
          )}
          {cliente.prioridadeSpc && (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2.5 py-1 font-medium text-rose-800">
              <AlertTriangle className="h-3 w-3" />
              Prioridade consulta SPC (&gt; 30 dias)
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Carteira" value={formatCurrency(cliente.valorCarteira)} />
          <Metric label="Vencido" value={formatCurrency(cliente.valorVencido)} />
          <Metric label="Atraso" value={`${Math.max(0, cliente.maxDaysOverdue)} dias`} />
          <Metric label="Vendedor" value={cliente.brokerName || "—"} />
        </div>

        <p className="text-xs text-slate-500">
          Régua visual (sem envio automático): lembrete antes do vencimento; 1º mês administrativa;
          2º mês notificação formal; 3º mês negativação SPC; 4º mês+ protesto ou jurídico.
        </p>

        <section className="rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-800">Status SPC</h3>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="flex min-w-[220px] flex-1 flex-col gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                Situação
              </span>
              <select
                value={spcStatus}
                onChange={(e) => setSpcStatus(e.target.value as SpcStatus)}
                className="h-10 rounded-lg border border-slate-200 bg-slate-50 px-2 text-sm text-slate-800"
              >
                {SPC_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{SPC_LABEL[opt]}</option>
                ))}
              </select>
            </label>
            <Button
              size="sm"
              variant="secondary"
              loading={saving}
              onClick={() => save({ spcStatus })}
            >
              Salvar SPC
            </Button>
            <Button
              size="sm"
              variant="ghost"
              loading={saving}
              onClick={() => save({ marcarBaixaSpc: true })}
            >
              Pedir baixa (5 dias úteis)
            </Button>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            {cliente.spcConsultadoEm ? `Consultado em ${formatDate(cliente.spcConsultadoEm)}. ` : null}
            {cliente.spcBaixaLimite
              ? `Baixa até ${formatDate(cliente.spcBaixaLimite)}.`
              : "Pagou: registrar o pedido de baixa em até 5 dias úteis."}
          </div>
        </section>

        <section className="rounded-xl border border-slate-200 p-4">
          <h3 className="text-sm font-semibold text-slate-800">Acordo / renegociação</h3>
          <textarea
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
            rows={2}
            placeholder="Observação do acordo"
            className="mt-3 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800"
          />
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              loading={saving}
              onClick={() => save({ acordoAtivo: true, acordoObservacao: observacao || null })}
            >
              Marcar em acordo
            </Button>
            <Button
              size="sm"
              variant="secondary"
              loading={saving}
              onClick={() => save({ acordoAtivo: false, acordoObservacao: observacao || null })}
            >
              Quebrar acordo
            </Button>
          </div>
        </section>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-slate-800">Boletos em aberto</h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full min-w-[640px] text-xs">
              <thead className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2">Título</th>
                  <th className="px-3 py-2">Parcela</th>
                  <th className="px-3 py-2">Condição</th>
                  <th className="px-3 py-2">Vencimento</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-right">Saldo</th>
                  <th className="px-3 py-2 text-right">Atraso</th>
                </tr>
              </thead>
              <tbody>
                {titulos.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                      Nenhum boleto em aberto.
                    </td>
                  </tr>
                )}
                {titulos.map((t) => (
                  <tr key={`${t.billId}-${t.installmentId ?? t.dueDate}`} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-slate-600">{t.billId}</td>
                    <td className="px-3 py-2">{t.installmentNumber ?? t.installmentId ?? "—"}</td>
                    <td className="px-3 py-2">{t.conditionType || "—"}</td>
                    <td className="px-3 py-2">{t.dueDate ? formatDate(t.dueDate) : "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatCurrency(t.originalAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{formatCurrency(t.balanceAmount)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{t.daysOverdue > 0 ? `${t.daysOverdue} d` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </Modal>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-slate-800">{value}</p>
    </div>
  );
}
